import { useCallback, useRef } from "react";
import { API } from "@apis/index";
import queryKeys from "@configs/react-query/query-key";
import { LOCAL_STORAGE } from "@constants/index";
import {
  computeLocalSyncMetadata,
  getStoredSyncMetadata,
  saveStoredSyncMetadataList,
  STORES,
  toSyncMetadataEpochMs,
} from "@utils/indexedDb";
import { getLocalStorage, setLocalStorage } from "@utils/local-storage";

const MIN_RECONCILE_INTERVAL_MS = 15_000;
const CROSS_TAB_GUARD_MS = 10_000;

let inFlightReconcile = null;

const normalizeServerSyncMetadata = (metadata) => ({
  entity: metadata?.entity,
  count: Number(metadata?.count) || 0,
  maxUpdatedAt: toSyncMetadataEpochMs(metadata?.maxUpdatedAt),
});

const syncMetadataMatch = (local, server) => {
  if (!local || !server) return false;
  if (local.count !== server.count) return false;
  return local.maxUpdatedAt === server.maxUpdatedAt;
};

const filterPersistableMetadata = (metadataList, allowedKeys) =>
  metadataList.filter((raw) => {
    const entity = raw?.entity;
    return entity && allowedKeys.has(entity) && entity !== STORES.SYNC_META;
  });

/**
 * Compare server sync metadata against IndexedDB and refetch only mismatched stores.
 * Used when SSE events may have been missed (offline, tab sleep, browser closed).
 */
export const useSseSyncReconciler = ({ syncEntityByKey, hasPermissionForResource, resources }) => {
  const lastLocalReconcileAtRef = useRef(0);

  const reconcile = useCallback(
    async ({ force = false } = {}) => {
      if (typeof syncEntityByKey !== "function") {
        return { changedStores: [], skipped: true };
      }

      if (inFlightReconcile) {
        return inFlightReconcile;
      }

      const now = Date.now();
      if (!force && now - lastLocalReconcileAtRef.current < MIN_RECONCILE_INTERVAL_MS) {
        return { changedStores: [], skipped: true, reason: "min-interval" };
      }

      const lastCrossTab = Number(getLocalStorage(LOCAL_STORAGE.LAST_RECONCILE_AT) || 0);
      if (!force && lastCrossTab && now - lastCrossTab < CROSS_TAB_GUARD_MS) {
        lastLocalReconcileAtRef.current = now;
        return { changedStores: [], skipped: true, reason: "cross-tab" };
      }

      inFlightReconcile = (async () => {
        try {
          setLocalStorage(LOCAL_STORAGE.LAST_RECONCILE_AT, String(now));
          lastLocalReconcileAtRef.current = now;

          const response = await API.get(queryKeys.getSyncMetadata);
          const serverMetadataList = Array.isArray(response?.data) ? response.data : [];
          const allowedResources = (resources || []).filter(
            (resource) => !hasPermissionForResource || hasPermissionForResource(resource),
          );
          const allowedKeys = new Set(allowedResources.map((r) => r.key));

          const changedStores = [];
          for (const raw of serverMetadataList) {
            const server = normalizeServerSyncMetadata(raw);
            if (!server.entity || !allowedKeys.has(server.entity)) {
              continue;
            }
            if (server.entity === STORES.SYNC_META) {
              continue;
            }

            // Fast path: the snapshot saved after the last sync/reconcile.
            const stored = await getStoredSyncMetadata(server.entity);
            if (syncMetadataMatch(stored, server)) {
              continue;
            }

            // The snapshot goes stale whenever an SSE delta writes straight to
            // IndexedDB, so check what the store actually holds before refetching it.
            const local = await computeLocalSyncMetadata(server.entity);
            if (!syncMetadataMatch(local, server)) {
              changedStores.push(server.entity);
            }
          }

          for (const storeKey of changedStores) {
            await syncEntityByKey(storeKey);
          }

          await saveStoredSyncMetadataList(filterPersistableMetadata(serverMetadataList, allowedKeys));

          if (changedStores.length > 0) {
            window.lastDataSyncTime = Date.now();
            window.dispatchEvent(
              new CustomEvent("dataSyncComplete", {
                detail: { syncTime: Date.now(), source: "sse-reconcile", changedStores },
              }),
            );
          }

          return { changedStores, skipped: false };
        } catch (error) {
          console.error("Failed to reconcile IndexedDB via sync metadata", error);
          return { changedStores: [], skipped: false, error };
        } finally {
          inFlightReconcile = null;
        }
      })();

      return inFlightReconcile;
    },
    [syncEntityByKey, hasPermissionForResource, resources],
  );

  return { reconcile };
};

export default useSseSyncReconciler;
